import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import { AiManagementCard, AiManagementState } from 'store/modules/ai_management_store/types';
import { getCards, removeCardLocal } from 'store/modules/ai_management_store/actions';
import { deleteCard } from 'services/actions/ai_management_cards';
import { formatDateTime } from 'services/ai_management_dates';
import LongText from 'components/atoms/long_text';
import AiManagementAvatar from 'components/atoms/ai_management_avatar';
import AiManagementUpdatesBell from 'components/atoms/ai_management_updates_bell';
import AiManagementCardMenu from 'components/molecules/ai_management_card_menu';
import AiManagementCardModal from 'components/organisms/ai_management_card_modal';
import AiManagementMembersModal from 'components/molecules/ai_management_members_modal';

interface AiManagementListRowProps {
  card: AiManagementCard;
  status: string;
  statusColor: string;
  selected: boolean;
  onToggle: () => void;
}

const AiManagementListRow: React.FC<AiManagementListRowProps> = ({
  card,
  status,
  statusColor,
  selected,
  onToggle,
}) => {
  const { bidding, members, counts } = card;
  const dispatch = useDispatch();
  const filters = useSelector<ApplicationState, AiManagementState['filters']>(
    (state) => state.session.ai_management.filters,
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [openAction, setOpenAction] = useState<'updates' | undefined>(undefined);
  const [membersModalOpen, setMembersModalOpen] = useState(false);

  const openModal = (action?: 'updates') => {
    setOpenAction(action);
    setModalOpen(true);
  };

  const handleRemove = () => {
    if (!window.confirm(`Remover a licitação "${bidding.notice_number}" do Gestão IA?`)) return;
    dispatch(removeCardLocal(card.id));
    deleteCard(card.id).then((res) => {
      if (!res || res.status >= 400) dispatch(getCards(filters));
    });
  };

  const dateRows = [
    { label: 'Prazo', iso: bidding.deadline_at },
    { label: 'Documento', iso: bidding.document_at },
    { label: 'Abertura', iso: bidding.opening_at },
  ].filter((row): row is { label: string; iso: string } => Boolean(row.iso));

  return (
    <>
      <tr>
        <td className="d-print-none">
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggle}
            aria-label={`Selecionar ${bidding.notice_number}`}
          />
        </td>
        <td className="font-weight-bold">{bidding.notice_number}</td>
        <td>
          <div className="list-orgao">
            <div className="font-weight-bold text-dark text-truncate">{bidding.public_body}</div>
            <div className="text-muted small text-truncate">
              {bidding.city} - {bidding.state}
            </div>
          </div>
        </td>
        <td>
          <div className="list-object">
            <LongText fullText={bidding.object} />
          </div>
        </td>
        <td>
          {dateRows.map((row) => (
            <div key={row.label} className="text-nowrap">
              <span className="font-weight-bold">{row.label}:</span> {formatDateTime(row.iso)}
            </div>
          ))}
        </td>
        <td>
          <span className="list-status" style={{ background: statusColor }}>
            {status}
          </span>
        </td>
        <td>
          <div className="d-flex align-items-center">
            <AiManagementUpdatesBell
              className="mr-2"
              count={counts.updates}
              onClick={() => openModal('updates')}
            />
            {members.map((member) => (
              <AiManagementAvatar
                key={member.id}
                initials={member.initials}
                memberId={member.id}
                name={member.name}
              />
            ))}
          </div>
        </td>
        <td className="text-right d-print-none">
          <AiManagementCardMenu
            ariaLabel={`Opções da licitação ${bidding.notice_number}`}
            items={[
              { label: 'Trabalhar na licitação', onClick: () => openModal() },
              { label: 'Alterar membros', onClick: () => setMembersModalOpen(true) },
              { label: 'Adicionar tarefa', onClick: () => {} },
              { label: 'Adicionar atualização', onClick: () => {} },
              {
                label: 'Remover do Gestão IA',
                onClick: handleRemove,
                danger: true,
                dividerBefore: true,
              },
            ]}
          />
        </td>
      </tr>

      {modalOpen && (
        <AiManagementCardModal
          card={card}
          initialAction={openAction}
          onClose={() => setModalOpen(false)}
        />
      )}

      <AiManagementMembersModal
        card={card}
        show={membersModalOpen}
        onClose={() => setMembersModalOpen(false)}
      />
    </>
  );
};

export default AiManagementListRow;
